import React from 'react';
import { inject, observer } from 'mobx-react';
import styles from './index.module.scss';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import Avatar from '@components/avatar';
import { BASE_IMG_URL } from '@images/imageUrls';
import classNames from 'classnames';
import { formatTime } from '@utils/timeUtils'; // 相对路径
import ThreadImgList from '@components/threadImgList';
import InfiniteScrollList from '@components/InfiniteScrollList';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
// 导入 Swiper 样式
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { highlightname } from '@utils/high-light-name';
import { setPostImage } from '@images/imageUrls';
import { formatNumberToWan } from '@utils/formatNumber';

interface PCThreadListProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    loading?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
    data: any
    likeClick?: Function
    actionType?: 'like' | 'favorite' // 操作类型：点赞/收藏
    actionClick?: Function // 操作点击事件
    goDetails?: Function
    goTopicDetails?: Function
    goUser?: Function
    recommendUserList?: any // 推荐关注用户列表
    showRecommendUserList?: any // 是否显示推荐关注用户
    createFollowUser?: any  // 关注用户事件
    followDelete?: any  // 取消关注用户事件
    searchKeyword?: any // 需要高亮的关键字
    emptyText?: string; // 为空时提示文字
    emptyIcon?: string; // 为空时icon
    emptyBoxStyle?: any; // 为空状态时的自定义样式
    isSelf?: boolean    // 是否是作者自己
    showThreadStatus?: any  // 是否显示帖子状态（为个人中心发帖列表加的）
    namespace?: string;
}

@inject('app', 'site', 'user')
@observer
class PCThreadList extends React.Component<PCThreadListProps> {
    componentDidMount() {
        // 可以在这里添加初始化逻辑
    }

    shouldShowStickyTag = (item: any) => {
        const { namespace = 'threadList' } = this.props;

        if (namespace === 'threadList') {
            return !!item.showIndex;
        }

        return item.isSticky != 0;
    }

    // 帖子标签
    tagRender = (item) => {
        let { isSelf, showThreadStatus, user } = this.props;
        const tags:any = [];
        
        // 如果 showThreadStatus 为 true，优先显示帖子状态
        if (showThreadStatus) {
            // 状态优先级：审核中 > 审核已拒绝 > 仅作者可见 > 已屏蔽 > 已沉贴
            if (item.isApproved == 0) {
                return (
                    <div className={styles.postUserRight}>
                        <div className={styles.postStatusBox}>
                            <img src={`${BASE_IMG_URL}/common/icon5.png`} alt="" className={styles.statusIcon} />
                            <div className={styles.statusText}>审核中</div>
                        </div>
                    </div>
                );
            }
            if (item.isApproved == 2) {
                return (
                    <div className={styles.postUserRight}>
                        <div className={styles.postErrorStatusBox}>
                            <img src={`${BASE_IMG_URL}/common/icon6.png`} alt="" className={styles.statusIcon} />
                            <div className={styles.statusText}>审核不通过</div>
                        </div>
                    </div>
                );
            }
            if (item.isHidden && item.isApproved == 1) {
                return (
                    <div className={styles.postUserRight}>
                        <div className={styles.postStatusBox}>
                            <img src={`${BASE_IMG_URL}/common/thread_hidden.png`} alt="" className={styles.statusIcon} />
                            <div className={styles.statusText}>{isSelf ? '仅自己可见' : '仅作者可见'}</div>
                        </div>
                    </div>
                );
            }
            
            if (!item.isDisplay && user?.userInfo?.group?.isAdmin && item.isApproved == 1) {
                return (
                    <div className={styles.postUserRight}>
                        <div className={styles.postStatusBox}>
                            <img src={`${BASE_IMG_URL}/common/thread_hidden.png`} alt="" className={styles.statusIcon} />
                            <div className={styles.statusText}>已屏蔽</div>    
                        </div>
                    </div>
                );
            }
            
            if (item.isSink && user?.userInfo?.group?.isAdmin && item.isApproved == 1) {
                return (
                    <div className={styles.postUserRight}>
                        <div className={styles.postStatusBox}>
                            <img src={`${BASE_IMG_URL}/common/thread_sink.png`} alt="" className={styles.statusIcon} />
                            <div className={styles.statusText}>已沉帖</div>
                        </div>
                    </div>
                );
            }
        }
        
        // 没有特殊状态时，正常显示标签
        // 第一优先级标签, 规则 最多显示2个标签，第一个标签优先级：爆>热>顶>精>荐>无； 
        if (item.isBoom) {
            tags.push({ img: `${BASE_IMG_URL}/common/fire.png`, alt: '爆款' });
        } else if (item.isHot) {
            tags.push({ img: `${BASE_IMG_URL}/common/hot.png`, alt: '热门' });
        } else if (this.shouldShowStickyTag(item)) {
            tags.push({ img: `${BASE_IMG_URL}/common/stick.png`, alt: '置顶' });
        } else if (item.isEssence) {
            tags.push({ img: `${BASE_IMG_URL}/common/essence.png`, alt: '精华' });
        } else if (item.isRecommend) {
            tags.push({ img: `${BASE_IMG_URL}/common/recommend.png`, alt: '推荐' });
        }
    
        // 赏标签（如果有）第二个标签优先级：赏>无
        if (item.isReward) {
            tags.push({ img: `${BASE_IMG_URL}/common/reward.png`, alt: '有赏' });
        }
    
        if (tags.length === 0) {
            return null;
        }
    
        return (
            <div className={styles.postUserRight}>
                {tags.map((tag, index) => (
                    <img
                        key={index}
                        src={tag.img}
                        alt={tag.alt}
                        className={styles.postTag}
                        title={tag.alt}
                    />
                ))}
            </div>
        );
    };

    // 问题反馈帖：内容前拼接状态标签
    feedbackStatusRender = (item: any) => {
        if (item.feedback == null) return null;
        const solveState = item.feedback.solveState;
        if (solveState == 0) {
            return <span className={classNames(styles.feedbackStatus, styles.feedbackStatus0)}>未解决</span>;
        }
        if (solveState == 1) {
            return <span className={classNames(styles.feedbackStatus, styles.feedbackStatus1)}>待处理</span>;
        }
        if (solveState == 2) {
            return <span className={classNames(styles.feedbackStatus, styles.feedbackStatus2)}>已回复</span>;
        }
        if (solveState == 3) {
            return <span className={classNames(styles.feedbackStatus, styles.feedbackStatus3)}>已解决</span>;
        }
        return null;
    };

    // 关注用户列表
    renderThreadUserList = () => {
        let { recommendUserList, createFollowUser, followDelete } = this.props;
        // console.log(recommendUserList, '更新')
        return (
            <div className={styles.threadUserListBox}>
                <Swiper
                    modules={[Navigation, Pagination, Autoplay]}
                    spaceBetween={12}
                    autoplay={false}
                    slidesPerView={5}  // 一页显示5个
                    slidesPerGroup={1} // 每次滑动1个
                    loop={false}
                    onSlideChange={() => console.log('slide change')}
                >
                    {recommendUserList.map((item: any, index:any) => (
                        <SwiperSlide key={item.id}>
                            <div className={styles.threadUserItem}>
                                <div className={styles.threadUserAvatarBox} onClick={() => {
                                    this.props.goUser && this.props.goUser(item)
                                }}>
                                    <img src={item.avatar} alt='用户头像' className={styles.threadUserAvatar} />
                                    <img src={`${BASE_IMG_URL}/common/user_bg1.png`} alt="" className={styles.userBg} />
                                    {item.titleMark == 2 && <img src={`${BASE_IMG_URL}/common/official1.png`} alt="" className={styles.threadUserOfficial} />}
                                    {item.titleMark == 1 && <img src={`${BASE_IMG_URL}/common/official2.png`} alt="" className={styles.threadUserOfficial} />}
                                </div>
                                <div className={styles.threadUserName}>{item.nickname}</div>
                                <div className={styles.threadUserTitle}>{item.titleName}</div>
                                {!item.isFollow && <div className={styles.threadUserFollow} onClick={(e) => {
                                    e.stopPropagation();
                                    window.globalEnsureLogin(() => {
                                        createFollowUser && createFollowUser(item, index)
                                    });
                                }}>关注</div>}
                                {item.isFollow && <div className={styles.threadUserFollowed} onClick={(e) => {
                                    e.stopPropagation();
                                    window.globalEnsureLogin(() => { 
                                        followDelete && followDelete(item, index)
                                    });
                                    
                                }}>已关注</div>}
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
                    
            </div>
        )
    }

    // 是否仍有未到点的定时发布
    isPendingFixedAt = (item: any) => {
        if (!item?.fixedAt) return false;
        const fixedDate = new Date(item.fixedAt).getTime();
        return !Number.isNaN(fixedDate) && fixedDate > Date.now();
    };

    // 渲染单个列表项
    renderThreadItem = (item: any, index: number) => {
        const { likeClick, actionType = 'like', actionClick, goDetails, goTopicDetails, goUser, showRecommendUserList, recommendUserList, searchKeyword, user, showThreadStatus, isSelf } = this.props;
        // console.log(showRecommendUserList, '是否显示推荐关注列表', recommendUserList)
        // 判断是否需要在第一条帖子后显示推荐用户列表
        const shouldShowRecommendUserList = showRecommendUserList &&
            recommendUserList?.length > 0 &&
            index === 4;

        const isAdmin = !!user?.userInfo?.group?.isAdmin;
        const isPendingFixedAt = this.isPendingFixedAt(item);
        // 管理员/本人：有未到点定时发布 → 只显示定时发布；否则所有人显示发布时间
        const showFixedAt = isPendingFixedAt && (isAdmin || !!isSelf);
        const showPostTime = !item.stickyPost && !isPendingFixedAt;

        return (
            <React.Fragment key={item.id || item._id || index}>
                <div className={styles.postItem} key={item.id || item._id}>
                    <div className={styles.postUserBox}>
                        <div className={styles.postUserLeft}>
                            <Avatar
                                avatarUrl={item.user?.avatar}
                                titleMark={item.user?.titleMark}
                                level={item.user?.level}
                                userCode={item.user?.code}
                                size={40}
                                officialSize={16}
                                levelBadgeWidth={18}
                                levelBadgeHeight={12}
                            ></Avatar>
                            <div className={styles.userDetail}>
                                <div
                                    className={styles.userNameBox}
                                    onClick={() => goUser && goUser(item.user)}
                                >
                                    <div className={styles.userName}>
                                        {item.user?.nickname || '普通用户'}
                                    </div>
                                    {item.user?.titleName && (
                                        <div className={styles.titleName}>
                                            {item.user?.titleName}
                                        </div>
                                    )}
                                </div>
                                <div className={styles.model}>{item.source}</div>
                            </div>
                        </div>
                        {/* <div className={styles.postUserRight}> */}
                        {/* 规则 最多显示2个标签，第一个标签优先级：爆>热>顶>精>无；第二个标签优先级：赏>无 */}
                        {!item.feedback && this.tagRender(item)}
                        {/* </div> */}
                    </div>

                    {/* 帖子图片 */}
                    <div
                        className={styles.postImgBox}
                        onClick={() => goDetails && goDetails(item)}
                    >
                        <ThreadImgList
                            cover={item.cover}
                            isVideo={item.video ? true : false}
                            videoData={item.video}
                            imageList={item.images}
                            maxDisplay={6}
                        ></ThreadImgList>
                    </div>

                    {item.title != '' && <div
                        className={styles.postTitle}
                        onClick={() => goDetails && goDetails(item)}
                        style={{color: (item.isHidden || (!item.isDisplay && user?.userInfo?.group?.isAdmin)) ? '#D1D3D4' :''}}
                        dangerouslySetInnerHTML={{ 
                            __html: highlightname(item.title, searchKeyword) || '用户名'
                        }}
                    >
                        {/* {item.title} */}
                    </div>}

                    {item.summary && <div
                        className={styles.postContent}
                        style={{color:  (item.isHidden || (!item.isDisplay && user?.userInfo?.group?.isAdmin)) ? '#D1D3D4' : item.title != '' ? '#6D6E71': 'inherit'}}
                        onClick={() => goDetails && goDetails(item)}
                    >
                        {this.feedbackStatusRender(item)}
                        <span dangerouslySetInnerHTML={{ 
                            __html: highlightname(item.summary, searchKeyword) || '用户名'
                        }} />
                    </div>}

                    <div className={styles.topicList}>
                        {item.topics?.map((topic: any) => (
                            <div
                                key={topic.id || topic._id}
                                className={styles.topicItem}
                                onClick={() => goTopicDetails && goTopicDetails(topic)}
                                style={{color: (item.isHidden || (!item.isDisplay &&  user?.userInfo?.group?.isAdmin)) ? '#D1D3D4' :''}}
                            >
                                # {topic.name}
                            </div>
                        ))}
                    </div>

                    {/* 有未到点定时发布时：只显示定时发布，不显示「发布于xxx」 */}
                    {showFixedAt && (() => {
                        const date = new Date(item.fixedAt);
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const hour = String(date.getHours()).padStart(2, '0');
                        const minute = String(date.getMinutes()).padStart(2, '0');
                        return (
                            <div className={styles.fixedAtBox}>
                                <img
                                    src={setPostImage.fixedAtIcon}
                                    alt="定时发布"
                                    className={styles.fixedAtIcon}
                                />
                                <span className={styles.fixedAtLabel}>定时发布：</span>
                                <span className={styles.fixedAtTime}>
                                    <span className={styles.fixedAtNum}>{year}</span> 年{' '}
                                    <span className={styles.fixedAtNum}>{month}</span> 月{' '}
                                    <span className={styles.fixedAtNum}>{day}</span> 日{' '}
                                    <span className={styles.fixedAtNum}>{hour}</span> 时{' '}
                                    <span className={styles.fixedAtNum}>{minute}</span> 分
                                </span>
                            </div>
                        );
                    })()}

                    <div className={styles.postOperaBox}>
                        <div className={styles.postOperaLeft}>
                            {/* 如果有置顶评论，显示置顶评论，没有置顶评论，显示发布时间 */}
                            {(item.stickyPost && !item.isHidden && item.isDisplay)  && <div className={styles.stickComment}>
                                <Avatar
                                    avatarUrl={item.stickyPost?.user?.avatar}
                                    showOfficialTag={false}
                                    size={18}
                                    titleMark={item.stickyPost?.user?.titleMark}
                                    userCode={item.stickyPost?.user?.code}
                                ></Avatar>
                                <div className={styles.commentContent}>
                                    {item.stickyPost.summary}
                                </div>
                            </div>}
                            {showPostTime && (
                                <div
                                    className={styles.postTime}
                                    style={{color: (item.isHidden || (!item.isDisplay && user?.userInfo?.group?.isAdmin)) ? '#D1D3D4' : ''}}
                                >
                                    {formatTime(item.createdAt)}
                                </div>
                            )}
                        </div>

                        {(() => {
                            // 定时发布未到点：不显示操作栏
                            if (isPendingFixedAt) {
                                return null;
                            }
                            return (
                                <div className={styles.operaItemBox}>
                                    {/* 阅读量显示规则是：只有在作者自己的发帖列表或者管理员且当前帖子无置顶评论时才显示 */}
                                    {((showThreadStatus && (isSelf || user?.userInfo?.group?.isAdmin)) && !item.stickyPost) &&<div
                                        className={styles.operaItem}
                                        onClick={() => goDetails && goDetails(item)}
                                    >
                                        <img
                                            src={`${BASE_IMG_URL}/common/view_icon.png`}
                                            alt="阅读量"
                                            className={styles.operaIcon}
                                        />
                                        <span className={styles.operaCount}>
                                            {formatNumberToWan(item.viewCount)}
                                        </span>
                                    </div>}
                                    <div
                                        className={styles.operaItem}
                                        onClick={() => goDetails && goDetails(item)}
                                    >
                                        <img
                                            src={`${BASE_IMG_URL}/common/comment.png`}
                                            alt="评论"
                                            className={styles.operaIcon}
                                        />
                                        <span className={styles.operaCount}>
                                            {item.postCount == 0 ? '首评' : formatNumberToWan(item.postCount)}
                                        </span>
                                    </div>

                                    {/* 根据 actionType 显示点赞或收藏按钮 */}
                                    {actionType === 'favorite' ? (
                                        <div
                                            className={styles.operaItem}
                                            onClick={() => window.globalEnsureLogin(() => actionClick && actionClick(item))}
                                        >
                                            {!item.isFavorite ? (
                                                <img
                                                    src={`${BASE_IMG_URL}/common/favorite.png`}
                                                    alt="收藏"
                                                    className={styles.operaIcon}
                                                />
                                            ) : (
                                                <img
                                                    src={`${BASE_IMG_URL}/common/favorited.png`}
                                                    alt="已收藏"
                                                    className={styles.operaIcon}
                                                />
                                            )}
                                            <span
                                                className={styles.operaCount}
                                                style={{ color: '#939598' }}
                                            >
                                                {item.favoriteCount == 0 ? '收藏' : formatNumberToWan(item.favoriteCount)}
                                            </span>
                                        </div>
                                    ) : (
                                        <div
                                            className={styles.operaItem}
                                            onClick={() => window.globalEnsureLogin(() => (actionClick || likeClick) && (actionClick || likeClick)(item))}
                                        >
                                            
                                            {!item.isLiked ? (
                                                <img
                                                    src={`${BASE_IMG_URL}/common/like.png`}
                                                    alt="点赞"
                                                    className={styles.operaIcon}
                                                />
                                            ) : (
                                                <img
                                                    src={`${BASE_IMG_URL}/common/liked.png`}
                                                    alt="已点赞"
                                                    className={styles.operaIcon}
                                                />
                                            )}
                                            <span
                                                className={styles.operaCount}
                                                style={{ color: item.isLiked ? '#980808' : '' }}
                                            >
                                                {item.likeCount == 0 ? '首赞' : formatNumberToWan(item.likeCount)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                </div>
                {/* 在第一条帖子后显示推荐用户列表 */}
                {shouldShowRecommendUserList && this.renderThreadUserList()}
            </React.Fragment>
        );
    };

    render() {
        const { data, loading = false, hasMore = true, emptyText, emptyIcon, emptyBoxStyle, onLoadMore } = this.props;

        if (!Array.isArray(data)) return null;

        return (
            <div className={styles.threadListBox}>
                <InfiniteScrollList
                    variant="pc"
                    data={data}
                    loading={loading}
                    hasMore={hasMore}
                    onLoadMore={onLoadMore || (() => { })}
                    renderItem={this.renderThreadItem}
                    threshold={100}
                    emptyText={emptyText || '这里是空的'}
                    emptyIcon={emptyIcon}
                    emptyBoxStyle={emptyBoxStyle}
                    loadingText="加载中..."
                    noMoreText="没有更多帖子了"
                />
            </div>
        );
    }
}

export default PCThreadList;
